import type { StoredApiCredentials, CredentialStorageResult } from '../../types/auth.types'

// Storage key for credentials
const CREDENTIALS_STORAGE_KEY = 'aml_guard_stored_credentials'

/**
 * Credential Storage Service
 * Manages API credentials mapped to phone numbers for local authentication
 * when the remote AML Guard API is unavailable
 */
export const credentialStorageService = {
  /**
   * Normalize phone number for consistent storage key
   */
  normalizePhone: (phone: string): string => {
    // Remove spaces, dashes, parentheses - keep only digits and leading +
    return phone.replace(/[\s\-\(\)]/g, '').trim()
  },

  /**
   * Get all stored credentials
   */
  getAllCredentials: (): StoredApiCredentials[] => {
    try {
      const stored = localStorage.getItem(CREDENTIALS_STORAGE_KEY)
      if (!stored) return []
      return JSON.parse(stored) as StoredApiCredentials[]
    } catch (error) {
      console.error('Failed to load stored credentials:', error)
      return []
    }
  },

  /**
   * Get credentials by phone number
   */
  getCredentialsByPhone: (phone: string): StoredApiCredentials | null => {
    const normalizedPhone = credentialStorageService.normalizePhone(phone)
    const credentials = credentialStorageService.getAllCredentials()
    return credentials.find(c =>
      credentialStorageService.normalizePhone(c.phone) === normalizedPhone
    ) || null
  },

  /**
   * Check if credentials exist for a phone number
   */
  hasCredentials: (phone: string): boolean => {
    return credentialStorageService.getCredentialsByPhone(phone) !== null
  },

  /**
   * Save credentials for a phone number
   */
  saveCredentials: (credentials: Omit<StoredApiCredentials, 'createdAt'>): CredentialStorageResult => {
    try {
      const normalizedPhone = credentialStorageService.normalizePhone(credentials.phone)
      const allCredentials = credentialStorageService.getAllCredentials()

      // Check if credentials already exist for this phone
      const existingIndex = allCredentials.findIndex(c =>
        credentialStorageService.normalizePhone(c.phone) === normalizedPhone
      )

      const newCredentials: StoredApiCredentials = {
        ...credentials,
        phone: normalizedPhone,
        createdAt: new Date().toISOString(),
      }

      if (existingIndex >= 0) {
        // Update existing
        allCredentials[existingIndex] = {
          ...newCredentials,
          createdAt: allCredentials[existingIndex].createdAt, // Keep original creation date
        }
      } else {
        // Add new
        allCredentials.push(newCredentials)
      }

      localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(allCredentials))

      return {
        success: true,
        message: existingIndex >= 0 ? 'Credentials updated' : 'Credentials saved',
        credentials: newCredentials,
      }
    } catch (error) {
      console.error('Failed to save credentials:', error)
      return {
        success: false,
        message: 'Failed to save credentials',
      }
    }
  },

  /**
   * Update last used timestamp for credentials
   */
  updateLastUsed: (phone: string): void => {
    const normalizedPhone = credentialStorageService.normalizePhone(phone)
    const allCredentials = credentialStorageService.getAllCredentials()

    const index = allCredentials.findIndex(c =>
      credentialStorageService.normalizePhone(c.phone) === normalizedPhone
    )

    if (index >= 0) {
      allCredentials[index].lastUsed = new Date().toISOString()
      localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(allCredentials))
    }
  },

  /**
   * Delete credentials for a phone number
   */
  deleteCredentials: (phone: string): boolean => {
    try {
      const normalizedPhone = credentialStorageService.normalizePhone(phone)
      const allCredentials = credentialStorageService.getAllCredentials()

      const filtered = allCredentials.filter(c =>
        credentialStorageService.normalizePhone(c.phone) !== normalizedPhone
      )

      if (filtered.length === allCredentials.length) {
        return false // Nothing was deleted
      }

      localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(filtered))
      return true
    } catch (error) {
      console.error('Failed to delete credentials:', error)
      return false
    }
  },

  /**
   * Clear all stored credentials
   */
  clearAllCredentials: (): void => {
    localStorage.removeItem(CREDENTIALS_STORAGE_KEY)
  },

  /**
   * Validate API token format (basic validation)
   */
  validateToken: (token: string): boolean => {
    // Token should be non-empty and reasonably long (JWT or API key)
    return token.length >= 20
  },

  /**
   * Validate user ID format
   */
  validateUserId: (userId: string): boolean => {
    return userId.length >= 3
  },
}

export default credentialStorageService
